import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav(next || "/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const seedDemo = async () => {
    try {
      const api = (await import("@/lib/api")).default;
      await api.post("/seed/demo");
      toast.success("Demo accounts ready · use demo.instructor@forge.lms / Demo123!");
    } catch {}
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2">
      <div className="hidden md:block relative">
        <img src="https://images.unsplash.com/photo-1686100510540-4c29a46de6e9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0A0A0A]/30" />
        <Link to="/" className="absolute top-8 left-8 text-white" data-testid="back-home">
          <span className="font-serif text-3xl">Forge<span className="text-[#E63946]">.</span></span>
        </Link>
        <div className="absolute bottom-12 left-8 right-8 text-white">
          <div className="overline text-[#E63946]">"</div>
          <p className="font-serif text-3xl leading-tight max-w-md">Forge replaced three vendors and our entire LMS RFP. The AI builder alone paid for it.</p>
          <div className="overline mt-4">— Head of L&D, Helios Bank</div>
        </div>
      </div>
      <div className="flex items-center justify-center px-8 py-12 bg-[#F9F9F8]">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-8">
            <Link to="/" className="font-serif text-3xl">Forge<span className="text-[#E63946]">.</span></Link>
          </div>
          <div className="overline mb-4">Sign in</div>
          <h1 className="editorial-h1 text-5xl mb-10">Welcome back.</h1>

          <button onClick={googleLogin} data-testid="google-login-button" className="w-full btn-secondary mb-6 justify-center">
            Continue with Google
          </button>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-[#E4E4E7]" />
            <span className="overline">or with email</span>
            <div className="flex-1 h-px bg-[#E4E4E7]" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="overline block mb-2">Email</label>
              <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-3 border border-[#E4E4E7] bg-white focus:border-[#0A0A0A] focus:outline-none transition-colors" />
            </div>
            <div>
              <label className="overline block mb-2">Password</label>
              <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-4 py-3 border border-[#E4E4E7] bg-white focus:border-[#0A0A0A] focus:outline-none transition-colors" />
            </div>
            <button type="submit" data-testid="login-submit" disabled={loading} className="btn-primary w-full justify-center mt-4">
              {loading ? "Signing in…" : <>Sign in <ArrowUpRight size={16} strokeWidth={1.5} /></>}
            </button>
          </form>

          <div className="mt-8 text-sm text-[#52525B]">
            New to Forge? <Link to="/signup" data-testid="link-signup" className="text-[#0A0A0A] underline underline-offset-4 hover:text-[#E63946]">Create an account</Link>
          </div>
          <button onClick={seedDemo} data-testid="seed-demo" className="mt-6 overline text-[#E63946] hover:underline">Load demo accounts →</button>
        </div>
      </div>
    </div>
  );
}
